"use client";

/**
 * Dev/preview handling for layout links that have no configured destination ("empty" links —
 * e.g. an "Emploi" or "Musée" nav button whose page isn't set up yet).
 *
 *   - PREVIEW (NEXT_PUBLIC_PREVIEW): the link stays visible but, instead of silently sending the
 *     visitor to the home page, clicking it opens a polished French modal explaining that the
 *     button isn't configured yet in this development preview and won't appear in production.
 *   - PRODUCTION: the link is removed from the DOM entirely (returns null), so unfinished buttons
 *     never reach real visitors.
 *
 * The mode is injected once by the host app via {@link DevPlaceholderProvider}; the SDK stays
 * environment-agnostic. Layout renderers use {@link GuardedLink} for leaf links and read
 * {@link useDevPlaceholder} to filter parent menus.
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Construction, Info, X } from "lucide-react";
import {
  isLivenessCheckableHref,
  normalizeHrefKey,
  resolveConfiguredHref,
  type LinkValue,
} from "../utils/links";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { RuntimeLink, type RuntimeLinkComponentProps } from "./RendererRuntime";

interface DevPlaceholderContextValue {
  /** True only on the preview site. When false (production / default), empty links are hidden. */
  previewMode: boolean;
  /** Open the dev modal for an unconfigured link, labelled with the link text. */
  notify: (label: string) => void;
  /** Normalized keys of the paths live on this target, or null when unknown (FAIL-OPEN:
   *  null means only empty/placeholder links are treated, never a non-empty slug). */
  liveHrefs: ReadonlySet<string> | null;
}

const DevPlaceholderContext = createContext<DevPlaceholderContextValue>({
  previewMode: false,
  notify: () => {},
  liveHrefs: null,
});

/** Access the preview flag + the modal trigger. */
export function useDevPlaceholder(): DevPlaceholderContextValue {
  return useContext(DevPlaceholderContext);
}

/** Provider: holds the modal state, renders the modal once, and exposes the preview flag. */
export function DevPlaceholderProvider({
  previewMode = false,
  liveHrefs = null,
  children,
}: {
  previewMode?: boolean;
  /** Paths live on this target (already normalized by the host, re-normalized here for
   *  safety). Passed as a plain array — a Set does not survive the RSC boundary. Null or
   *  omitted = liveness unknown = fail-open. */
  liveHrefs?: readonly string[] | null;
  children: React.ReactNode;
}) {
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const value = useMemo<DevPlaceholderContextValue>(
    () => ({
      previewMode,
      notify: (label: string) => setActiveLabel(label ?? ""),
      liveHrefs: liveHrefs && liveHrefs.length > 0 ? new Set(liveHrefs.map((p) => normalizeHrefKey(p))) : null,
    }),
    [previewMode, liveHrefs],
  );

  return (
    <DevPlaceholderContext.Provider value={value}>
      {children}
      <DevPlaceholderModal open={activeLabel !== null} label={activeLabel ?? ""} onClose={() => setActiveLabel(null)} />
    </DevPlaceholderContext.Provider>
  );
}

export type GuardedHrefStatus = "ok" | "placeholder" | "hidden";

/**
 * Full link verdict: configuration (empty / "/" / "#" placeholders) AND liveness (a non-empty
 * internal slug whose page does not exist on this target). "ok" → render a normal link;
 * "placeholder" (preview only) → keep it visible but open the dev modal on click;
 * "hidden" (production) → the link must not reach visitors.
 * FAIL-OPEN: with no liveness set (null), or for non-checkable hrefs (external, mailto,
 * storage assets with a file extension), a resolved href is always "ok".
 */
export function useGuardedHref(target: LinkValue): { status: GuardedHrefStatus; href?: string } {
  const { previewMode, liveHrefs } = useDevPlaceholder();
  const resolved = resolveConfiguredHref(target);
  if (!resolved) return { status: previewMode ? "placeholder" : "hidden" };
  if (!isLivenessCheckableHref(resolved)) return { status: "ok", href: resolved };
  if (liveHrefs === null) return { status: "ok", href: resolved };
  if (liveHrefs.has(normalizeHrefKey(resolved))) return { status: "ok", href: resolved };
  return { status: previewMode ? "placeholder" : "hidden" };
}

/** Props for {@link GuardedLink}: a RuntimeLink that handles the "no destination" case. */
export type GuardedLinkProps = RuntimeLinkComponentProps & {
  /** Visible link text — shown in the dev modal when the link has no destination. */
  label: string;
  /** Production behavior for an unresolved link: "hide" (default) removes it from the DOM;
   *  "unwrap" keeps the content in a plain <span> — the text survives, the link dies.
   *  Use "unwrap" wherever the label IS content (document lists, cards with text). */
  fallback?: "hide" | "unwrap";
  /** Content of the unwrapped <span> when it must differ from the link (e.g. a document
   *  label without its download icon). Defaults to children ?? label. */
  unwrapChildren?: React.ReactNode;
  /** className of the unwrapped <span> when the link styling (underline, hover) must not
   *  survive on dead text. Defaults to className. */
  unwrapClassName?: string;
  /** True when the children are block/card content: the preview placeholder then renders a
   *  <span role="button"> instead of a <button> (a <button> cannot contain flow content). */
  block?: boolean;
};

/**
 * A leaf link that respects the dev/prod rules:
 *   - resolvable AND live href → renders a normal RuntimeLink;
 *   - unresolved/dead href in PREVIEW → stays visible, opens the dev modal (no navigation);
 *   - unresolved/dead href in PRODUCTION → hidden (or unwrapped to plain content).
 * Relies on Tailwind Preflight resetting `<button>` to inherit font/color so the button reads
 * identically to the link it replaces.
 */
export function GuardedLink({ href, label, children, onClick, className, fallback = "hide", block = false, unwrapChildren, unwrapClassName, ...rest }: GuardedLinkProps) {
  const { notify } = useDevPlaceholder();
  const guarded = useGuardedHref(href as LinkValue);

  if (guarded.status === "ok") {
    return (
      <RuntimeLink href={guarded.href!} className={className} onClick={onClick} {...rest}>
        {children ?? label}
      </RuntimeLink>
    );
  }

  // "unwrap" = the content is INFORMATIVE (person cards, document titles, org cards): it must
  // stay visible everywhere and simply lose the link when the target doesn't exist. That rule
  // applies in preview too — an informative card must not pose as a configurable button, so no
  // dev modal: preview and production render the same inert content.
  if (fallback === "unwrap") {
    return <span className={unwrapClassName ?? className}>{unwrapChildren ?? children ?? label}</span>;
  }

  if (guarded.status === "hidden") {
    return null;
  }

  // Preview placeholder. Anchor-only attributes don't belong on a <button>/<span>.
  const { target: _target, rel: _rel, ...buttonRest } = rest as Record<string, unknown>;
  const activate = (event: React.SyntheticEvent) => {
    onClick?.(event as unknown as React.MouseEvent<HTMLAnchorElement>);
    notify(label);
  };

  if (block) {
    // Cards/tiles: flow content is not valid inside <button>, so use an accessible span.
    return (
      <span
        role="button"
        tabIndex={0}
        data-dev-placeholder="true"
        className={`cursor-pointer ${className ?? ""}`}
        onClick={activate}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            activate(event);
          }
        }}
        {...(buttonRest as React.HTMLAttributes<HTMLSpanElement>)}
      >
        {children ?? label}
      </span>
    );
  }

  return (
    <button
      type="button"
      data-dev-placeholder="true"
      className={`cursor-pointer ${className ?? ""}`}
      onClick={activate}
      {...(buttonRest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {children ?? label}
    </button>
  );
}

/** The premium French "not configured yet" modal shown in preview. */
function DevPlaceholderModal({ open, label, onClose }: { open: boolean; label: string; onClose: () => void }) {
  // Keep mounted briefly after close so the exit transition can play.
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    setEntered(false);
    const timeout = setTimeout(() => setMounted(false), 220);
    return () => clearTimeout(timeout);
  }, [open]);

  useBodyScrollLock(mounted);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="bcc-dev-modal-title">
      <div
        className={`absolute inset-0 bg-[#0a1f3a]/55 backdrop-blur-sm transition-opacity duration-200 ${entered ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />
      <div
        /* maxWidth is set inline (not via a Tailwind arbitrary class) so the card is ALWAYS
           constrained even if the dev stylesheet hasn't re-scanned new classes yet — avoids the
           full-width blow-up on a stale CSS cache. Everything else uses standard utilities or
           brand hexes already present in the compiled CSS. */
        style={{ maxWidth: "26rem" }}
        className={`relative w-full overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 transition-all duration-200 ${
          entered ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-95 opacity-0"
        }`}
      >
        {/* Slim brand accent — a single hairline of gradient instead of an oversized header band. */}
        <div className="h-1 w-full bg-gradient-to-r from-[#005CA9] via-[#0073C4] to-[#0095DB]" />

        <div className="px-6 pb-6 pt-5">
          {/* Top row: a single icon + context tag on the left, close on the right. */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-amber-50 ring-1 ring-amber-200">
                <Construction className="h-5 w-5 text-amber-500" />
              </span>
              <span className="inline-flex items-center rounded-full bg-[#F4F8FC] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-[#005CA9]">
                Aperçu — développement
              </span>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="-mr-1 -mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <h2 id="bcc-dev-modal-title" className="mt-4 text-lg font-extrabold leading-snug tracking-tight text-[#0a2a4d]">
            Bouton en cours de configuration
          </h2>
          {label ? <p className="mt-1 text-sm font-semibold text-[#005CA9]">«&nbsp;{label}&nbsp;»</p> : null}
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Ce lien n’a pas encore de destination configurée dans cet environnement de prévisualisation.
            Il reste visible ici pour les tests, mais&nbsp;
            <span className="font-semibold text-slate-800">il ne sera pas affiché sur le site de production</span>
            &nbsp;tant qu’il n’aura pas été configuré.
          </p>
          <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-[#F4F8FC] px-3.5 py-3 text-[13px] leading-relaxed text-[#005CA9]">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Configurez la destination de ce bouton dans l’administration pour l’activer.</span>
          </div>
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-xl bg-[#005CA9] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0a4a8a]"
            >
              J’ai compris
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
